import cx from 'classnames'
import { ButtonHTMLAttributes, forwardRef, useId } from 'react'

import {
  SanityContentBlockButtonColor,
  SanityContentBlockButtonIconAlignment,
  SanityContentBlockButtonSize,
  SanityContentBlockButtonVariant,
} from '@data/sanity/queries/types/content'

import Icon, { IconName } from '@components/icon'

export type ButtonVariant = 'primary' | 'secondary' | 'link'

export type ButtonSize =
  | 'default'
  | 'small'
  | 'normal-small'
  | 'normal'
  | 'large'

export type ButtonColor =
  | 'default'
  | 'white'
  | 'black'
  | 'gray-light'
  | 'yellow'
  | 'pink'
  | 'red'

export type ButtonIconAlignment = 'left' | 'right'

export type ButtonIconSize = 'default' | 'small' | 'normal' | 'large'

type VariantColorClassMap = Record<
  ButtonVariant,
  Record<ButtonColor | 'default', string | string[]>
>

export type SizeClassMap = Record<ButtonSize, string | string[]>

export interface ButtonProps {
  variant?: ButtonVariant
  size?: ButtonSize
  color?: ButtonColor
  icon?: IconName
  iconSize?: ButtonIconSize
  iconAlignment?: ButtonIconAlignment
  isActive?: boolean
}

export const getBaseClasses = (variant?: ButtonVariant) =>
  cx('inline-flex items-center disabled:opacity-50', {
    'justify-center rounded-full text-center border transition-all duration-300 font-medium':
      variant !== 'link',
  })

export const colorClasses: VariantColorClassMap = {
  primary: {
    white: ['border-white bg-white text-black'],
    black: ['border-black bg-black text-white'],
    'gray-light': ['border-gray-light bg-gray-light text-black'],
    yellow: [
      'border-yellow bg-yellow text-black',
      'hover:bg-yellow-dark hover:border-yellow-dark',
    ],
    pink: [
      'border-pink bg-pink text-black',
      'hover:bg-pink-dark hover:border-pink-dark',
    ],
    red: [
      'border-red bg-red text-white',
      'hover:bg-red-dark hover:border-red-dark',
    ],
    default: [
      'bg-btn-primary-bg text-btn-primary-text border-btn-primary-border',
      'hover:bg-btn-primary-bg-hover hover:text-btn-primary-text-hover hover:border-btn-primary-border-hover',
    ],
  },
  secondary: {
    white: [
      'bg-transparent border-white text-white',
      'hover:bg-white hover:text-black',
    ],
    black: [
      'bg-transparent border-black text-black',
      'hover:bg-black hover:text-white',
    ],
    'gray-light': [
      'bg-transparent border-gray-light text-black',
      'hover:bg-gray-light',
    ],
    yellow: [
      'bg-transparent border-yellow text-yellow',
      'hover:bg-yellow hover:text-black',
    ],
    pink: [
      'bg-transparent border-pink text-pink',
      'hover:bg-pink hover:text-black',
    ],
    red: [
      'bg-transparent border-red text-red',
      'hover:bg-red hover:text-white',
    ],
    default: [
      'bg-btn-secondary-bg border border-btn-secondary-border text-btn-secondary-text',
      'hover:bg-btn-secondary-bg-hover hover:text-btn-secondary-text-hover hover:border-btn-secondary-border-hover',
    ],
  },
  link: {
    white: ['text-white'],
    black: ['text-black'],
    'gray-light': ['text-gray-light'],
    yellow: ['text-yellow'],
    pink: ['text-pink'],
    red: ['text-red'],
    default: ['text-current'],
  },
}

const sizeClasses: SizeClassMap = {
  default: '',
  small: 'text-base',
  'normal-small': 'text-base',
  normal: 'text-xl',
  large: 'text-3xl',
}

const spacingClasses: SizeClassMap = {
  default: '',
  small: 'px-4 py-1',
  'normal-small': 'px-6 py-3',
  normal: 'px-12 py-4',
  large: 'px-12 py-4',
}

export const iconSizeClasses: SizeClassMap = {
  default: '',
  small: 'text-xl',
  'normal-small': 'text-xl',
  normal: 'text-2xl',
  large: 'text-3xl',
}

const activeClasses: VariantColorClassMap = {
  primary: {
    white: 'border-white bg-white text-black',
    black: 'border-black bg-black text-white',
    'gray-light': 'border-gray-light bg-white text-black',
    yellow: 'border-yellow-dark bg-yellow-dark text-black',
    pink: 'border-pink-dark bg-pink-dark text-black',
    red: 'border-red-dark bg-red-dark text-white',
    default:
      'bg-btn-primary-bg-hover text-btn-primary-text-hover border-btn-primary-border-hover',
  },
  secondary: {
    white: 'border-white bg-white text-black',
    black: 'border-black bg-black text-white',
    'gray-light': 'border-gray-light bg-white text-black',
    yellow: 'border-yellow bg-yellow text-black',
    pink: 'border-pink bg-pink text-black',
    red: 'border-red bg-red text-white',
    default:
      'bg-btn-secondary-bg-hover text-btn-secondary-text-hover border-btn-secondary-border-hover',
  },
  link: colorClasses.link,
}

export const getButtonVariant = (variant?: SanityContentBlockButtonVariant) => {
  if (!variant) {
    return
  }

  return variant as ButtonVariant
}

export const getButtonSize = (
  size?: SanityContentBlockButtonSize,
): ButtonSize => {
  if (!size) {
    return 'default'
  }

  return size
}

export const getButtonColor = (
  color?: SanityContentBlockButtonColor,
): ButtonColor | undefined => {
  if (!color) {
    return
  }

  return color
}

export const getButtonIconAlignment = (
  iconAlignment?: SanityContentBlockButtonIconAlignment,
): ButtonIconAlignment | undefined => {
  if (!iconAlignment) {
    return
  }

  return iconAlignment
}

/**
 * Merge classes that match a given regular expression.
 * Example: `mergeClassesByRegExps('bg-one text-one bg-two', [/^bg-.+$/])` will return `'text-one bg-two'`.
 */
const mergeClassesByRegExps = (classes: string, groupRegExps: RegExp[]) => {
  const newClasses = classes.split(' ')
  const redundantClasses: string[] = []

  groupRegExps.forEach((groupRegExp) => {
    const filteredClasses = newClasses.filter((newClass) =>
      groupRegExp.test(newClass),
    )

    if (filteredClasses.length >= 2) {
      // Add matching classes to the redundant class list (except the last one)
      filteredClasses.slice(0, -1).forEach((filteredClass) => {
        redundantClasses.push(filteredClass)
      })
    }

    return
  })

  return newClasses
    .filter((newClass) => !redundantClasses.includes(newClass))
    .join(' ')
}

export const getButtonStyles = ({
  variant = 'link',
  size = 'normal',
  color = 'default',
  isActive,
}: ButtonProps) => {
  return mergeClassesByRegExps(
    cx(
      getBaseClasses(variant),
      colorClasses[variant][color],
      isActive ? activeClasses[variant][color] : '',
      variant !== 'link' ? spacingClasses[size] : '',
      sizeClasses[size],
    ),
    [/^bg-.+$/],
  )
}

export interface ButtonIconProps {
  name: IconName
  alignment?: ButtonIconAlignment
  className?: string
}

export const ButtonIcon = ({
  name,
  alignment = 'right',
  className,
}: ButtonIconProps) => {
  const id = useId()

  return (
    <span
      className={cx(
        {
          'order-first mr-3': alignment === 'left',
          'ml-4': alignment === 'right',
        },
        className,
      )}
    >
      <Icon id={`button-icon-${id}`} name={name} />
    </span>
  )
}

const Button = forwardRef<
  HTMLButtonElement,
  ButtonProps & ButtonHTMLAttributes<HTMLButtonElement>
>(
  (
    {
      id,
      type,
      'aria-label': ariaLabel,
      variant,
      size,
      color,
      icon,
      iconSize,
      iconAlignment,
      isActive,
      disabled,
      onClick,
      onBeforeInput,
      className,
      style,
      children,
    }: ButtonProps & ButtonHTMLAttributes<HTMLButtonElement>,
    ref,
  ) => {
    return (
      <button
        ref={ref}
        id={id}
        type={type}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={onClick}
        onBeforeInput={onBeforeInput}
        className={cx(
          getButtonStyles({ variant, size, color, isActive }),
          className,
        )}
        style={style}
      >
        {children}
        {icon && (
          <ButtonIcon
            name={icon}
            alignment={iconAlignment}
            className={cx(iconSize ? iconSizeClasses[iconSize] : {})}
          />
        )}
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button
